import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgIf } from '@angular/common';

/**
 * Bottom sheet overlay. Stays mounted through its exit animation.
 * On desktop it becomes a centered modal dialog (`deskW` sets its width).
 */
@Component({
  selector: 'app-sheet',
  standalone: true,
  imports: [NgIf],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .bb-sheet-scrim {
      position: absolute; inset: 0; z-index: 200; display: flex; align-items: flex-end; justify-content: center;
      transition: background .28s ease;
    }
    .bb-sheet-card {
      width: 100%; max-width: 520px; background: var(--fr-surface); border-radius: 24px 24px 0 0;
      transition: transform .32s cubic-bezier(.32,.72,0,1), opacity .22s ease; overflow: hidden; display: flex;
      flex-direction: column; box-shadow: 0 -8px 40px rgba(0,0,0,0.18);
      transform: translateY(101%);
    }
    .bb-sheet-card.bb-open { transform: translateY(0); }

    /* Desktop: centered dialog that fades/pops instead of sliding up */
    :host-context(.fr-root.bb-desktop) .bb-sheet-scrim { align-items: center; padding: 24px; }
    :host-context(.fr-root.bb-desktop) .bb-sheet-card {
      border-radius: 20px; box-shadow: 0 30px 80px -20px rgba(0,0,0,0.5);
      transform: translateY(14px) scale(.98); opacity: 0;
      transition: transform .2s cubic-bezier(.2,.9,.3,1), opacity .18s ease;
    }
    :host-context(.fr-root.bb-desktop) .bb-sheet-card.bb-open { transform: none; opacity: 1; }
  `],
  template: `<div *ngIf="show" class="bb-sheet-scrim" (click)="onClose()"
      [style.background]="open ? 'rgba(16,12,8,0.42)' : 'rgba(16,12,8,0)'"
      [style.backdrop-filter]="open ? 'blur(2px)' : 'none'"
      (transitionend)="onScrimTransitionEnd()">
      <div class="bb-sheet-card" [class.bb-open]="open" (click)="$event.stopPropagation()"
        [style.max-height]="maxH" [style.height]="height" [style.max-width.px]="deskW">
        <div [style.padding]="pad ? '4px 0 0' : '0'" style="overflow-y:auto;flex:1">
          <ng-content></ng-content>
        </div>
        <ng-content select="[sheetFooter]"></ng-content>
      </div>
    </div>`,
})
export class SheetComponent {
  @Input() height = 'auto';
  @Input() maxH = '92%';
  @Input() pad = true;
  /** Dialog width on desktop (phones always use the full sheet width, capped at 520). */
  @Input() deskW = 520;
  @Output() close = new EventEmitter<void>();

  show = false;
  private _open = false;

  @Input() set open(v: boolean) {
    this._open = v;
    if (v) this.show = true;
  }
  get open(): boolean { return this._open; }

  onClose(): void { this.close.emit(); }
  onScrimTransitionEnd(): void { if (!this._open) this.show = false; }
}
